import WhatsApp from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { MediaIconButton } from '@/components/atoms';
import { useWhatsAppLink } from '@/hooks/useWhatsAppLink';
import { type VehicleListing } from '@/services/vehicles/types';

import { AvailabilityChip } from './AvailabilityChip';
import { CarQuickAddButton } from './CarQuickAddButton';
import { ShareVehicleButton } from './ShareVehicleButton';
import { VehiclePhoto } from './VehiclePhoto';

type Props = {
  vehicle: VehicleListing;
  title: string;
  whatsappMessage: string;
  /** Sold cars show the photo, the state and share, and nothing that invites a purchase. */
  isSold: boolean;
};

/**
 * The top half of a listing card: the photograph, the availability state and
 * the secondary actions: share, add to cart and WhatsApp.
 *
 * The photograph is not itself a link. The card's single link is the title,
 * stretched across the whole card (see VehicleCard), and these controls sit
 * above it — hence `zIndex`, which keeps them clickable rather than swallowed
 * by that overlay.
 */
export function VehicleCardMedia({ vehicle, title, whatsappMessage, isSold }: Props) {
  /* The message names this car, the number comes from the page the grid is on
     — /cars and /vehicles both route to sales. */
  const whatsappHref = useWhatsAppLink({ message: whatsappMessage });

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      <Box
        className="vehicle-card__photo"
        sx={{
          transition: 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <VehiclePhoto
          image={vehicle.cover}
          fallbackAlt={title}
          sizes="(min-width: 1200px) 300px, (min-width: 900px) 33vw, (min-width: 600px) 50vw, 100vw"
        />
      </Box>

      <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
        <AvailabilityChip availability={vehicle.availability} />
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
      >
        <ShareVehicleButton vehicle={vehicle} />
        {!isSold && <CarQuickAddButton vehicle={vehicle} />}
        {!isSold && whatsappHref && (
          <MediaIconButton
            label={`Ask about the ${title} on WhatsApp (opens in a new tab)`}
            tone="whatsapp"
            icon={<WhatsApp />}
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
          />
        )}
      </Stack>
    </Box>
  );
}
